import { z } from 'zod';
import { AGE_GROUPS, APPROVAL_METHODS, GRADES, ROLES } from './constants';
import { QR_MAX_LENGTH } from './qr';

export const objectId = z.string().regex(/^[a-f0-9]{24}$/i, 'Invalid id');
const email = z.string().trim().toLowerCase().max(254).pipe(z.email());
const personName = z.string().trim().min(1).max(80);
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a 6-digit hex color like #cc0310');

export const PASSWORD_MIN_LENGTH = 10;
export const password = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(200);

// ── Auth ────────────────────────────────────────────────────────────────────
export const loginSchema = z.object({ email, password: z.string().min(1).max(200) });
export const refreshSchema = z.object({ refreshToken: z.string().min(20).max(200).optional() });
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: password,
});
export const passwordResetRequestSchema = z.object({ email });
export const passwordResetConfirmSchema = z.object({
  token: z.string().min(20).max(200),
  newPassword: password,
});
export const acceptInvitationSchema = z.object({
  token: z.string().min(20).max(200),
  name: personName,
  password,
});

// ── Users & invitations ─────────────────────────────────────────────────────
export const ageGroupSchema = z.enum(AGE_GROUPS);
export const leaderboardQuerySchema = z.object({ group: z.enum(AGE_GROUPS).optional() });
export const createInvitationSchema = z.object({
  email,
  name: personName,
  role: z.enum(ROLES).default('student'),
  /** Students only: which curriculum and leaderboards they belong to. */
  ageGroup: ageGroupSchema.default('adults'),
  /** Parents only: the children they'll follow once they accept. */
  studentIds: z.array(objectId).max(20).optional(),
});
/** A parent for a student: linked straight away if the email already has a parent account. */
export const addParentSchema = z.object({ email, name: personName });
/**
 * A child without a login of their own (for young kids): a parent follows them in the app and shows
 * their QR code in class.
 */
export const managedStudentSchema = z.object({
  name: personName,
  ageGroup: ageGroupSchema.default('kids'),
  parentName: personName,
  parentEmail: email,
});
export const updateUserSchema = z
  .object({
    name: personName,
    role: z.enum(ROLES),
    ageGroup: ageGroupSchema,
    /** Students: keep off the leaderboards. */
    leaderboardHidden: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const statusChangeSchema = z.object({ reason: z.string().trim().max(500).optional() });
export const deleteAccountSchema = z.object({ password: z.string().min(1).max(200) });
/** An Expo push token for this device, registered so progress milestones can be celebrated. */
export const pushTokenSchema = z.object({
  token: z
    .string()
    .max(200)
    .regex(/^Expo(nent)?PushToken\[[^\]]+\]$/, 'Not an Expo push token'),
  platform: z.enum(['ios', 'android']),
});
export const removePushTokenSchema = pushTokenSchema.pick({ token: true });
/** Profile pictures are square JPEGs made small on the phone; this caps the decoded size. */
export const AVATAR_MAX_BYTES = 300_000;
export const avatarSchema = z.object({
  /** Base64-encoded JPEG. */
  image: z
    .string()
    .min(100)
    .max(Math.ceil((AVATAR_MAX_BYTES * 4) / 3) + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Not a base64 image'),
});

/** An instructor's or owner's signature for certificates: a small PNG drawn in the dashboard. */
export const SIGNATURE_MAX_BYTES = 100_000;
export const signatureSchema = z.object({
  /** Base64-encoded PNG. */
  image: z
    .string()
    .min(100)
    .max(Math.ceil((SIGNATURE_MAX_BYTES * 4) / 3) + 4)
    .regex(/^[A-Za-z0-9+/]+={0,2}$/, 'Not a base64 image'),
});
export const issueCertificateSchema = z.object({ studentId: objectId });

// ── Curriculum ──────────────────────────────────────────────────────────────
// Update schemas are built from field definitions *without* defaults: in Zod 4, `.partial()` still
// applies `.default()` values, which would silently overwrite fields omitted from a PATCH.
const categoryFields = {
  name: z.string().trim().min(1).max(80),
  /** Whose curriculum the category (and its techniques) belongs to. */
  ageGroup: ageGroupSchema,
  description: z.string().trim().max(1000),
  order: z.number().int().min(0).max(100000),
  active: z.boolean(),
};
export const categorySchema = z.object({
  ...categoryFields,
  description: categoryFields.description.default(''),
  order: categoryFields.order.default(0),
  active: categoryFields.active.default(true),
  ageGroup: categoryFields.ageGroup.default('adults'),
});
export const updateCategorySchema = z.object(categoryFields).partial();

export const techniqueKey = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9-]{0,79}$/, 'Use lowercase letters, digits and dashes');

const techniqueFields = {
  key: techniqueKey,
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(5000),
  categoryId: objectId,
  order: z.number().int().min(0).max(100000),
  videoAssetId: objectId.nullable(),
  prerequisiteIds: z.array(objectId).max(50),
  /** Visible to students, gradable and counted towards progress. */
  active: z.boolean(),
};
export const techniqueSchema = z.object({
  ...techniqueFields,
  // Generated from the name when omitted (the dashboard never asks for it).
  key: techniqueFields.key.optional(),
  description: techniqueFields.description.default(''),
  order: techniqueFields.order.default(0),
  videoAssetId: techniqueFields.videoAssetId.default(null),
  prerequisiteIds: techniqueFields.prerequisiteIds.default([]),
  // New techniques start hidden until the owner turns them on.
  active: techniqueFields.active.default(false),
});
export const updateTechniqueSchema = z.object(techniqueFields).partial();
export const setPrerequisitesSchema = z.object({ prerequisiteIds: z.array(objectId).max(50) });
/** A complete new display order: every id in the list, first to last. */
export const reorderSchema = z.object({
  ids: z
    .array(objectId)
    .min(1)
    .max(2000)
    .refine((ids) => new Set(ids).size === ids.length, 'Each id may appear only once'),
});
export const curriculumImportSchema = z.object({
  csv: z.string().min(1).max(2_000_000),
  dryRun: z.boolean().default(false),
});

// ── Approvals ───────────────────────────────────────────────────────────────
export const gradeSchema = z.enum(GRADES);
/** Feedback on a grading, shown to the student. Plain text. */
export const GRADING_NOTE_MAX_LENGTH = 1000;
const gradingNoteText = z.string().trim().min(1).max(GRADING_NOTE_MAX_LENGTH);
export const scanApprovalSchema = z.object({
  techniqueId: objectId,
  grade: gradeSchema,
  qrPayload: z.string().max(QR_MAX_LENGTH),
  /** Client-generated id for tracing a scan through logs; idempotency is enforced server-side. */
  clientScanId: z.string().max(64).optional(),
  /**
   * When the scan happened, for scans saved offline and sent later. The grade keeps this time, and a
   * late scan never overrides a grade recorded after it.
   */
  scannedAt: z.iso.datetime().optional(),
  /** Feedback saved with the grade (e.g. a quick note picked on the scan screen). */
  note: gradingNoteText.optional(),
});
export const manualApprovalSchema = z.object({
  studentId: objectId,
  techniqueId: objectId,
  grade: gradeSchema,
  /** Owner-only: bypass prerequisite enforcement when raising a grade. Requires a reason; audited. */
  override: z.boolean().default(false),
  reason: z.string().trim().max(500).optional(),
  note: gradingNoteText.optional(),
});
export const gradingNoteSchema = z.object({ text: gradingNoteText });
/** Owner-defined feedback instructors can pick instead of typing (e.g. "Keep your elbows in"). */
export const QUICK_NOTE_MAX_LENGTH = 200;
export const MAX_QUICK_NOTES = 50;
export const quickNoteSchema = z.object({
  text: z.string().trim().min(1).max(QUICK_NOTE_MAX_LENGTH),
});
export const rescindApprovalSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const approvalQuerySchema = z.object({
  studentId: objectId.optional(),
  techniqueId: objectId.optional(),
  instructorId: objectId.optional(),
  method: z.enum(APPROVAL_METHODS).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: z.iso.datetime().optional(),
});

// ── Videos ──────────────────────────────────────────────────────────────────
export const createYouTubeVideoSchema = z.object({
  title: z.string().trim().min(1).max(160),
  url: z.string().trim().min(1).max(500),
});

// ── Settings ────────────────────────────────────────────────────────────────
export const gymSettingsSchema = z.object({
  gymName: z.string().trim().min(1).max(120),
  shortName: z.string().trim().min(1).max(30),
  appDisplayName: z.string().trim().min(1).max(30),
  curriculumTitle: z.string().trim().min(1).max(80),
  kidsCurriculumTitle: z.string().trim().min(1).max(80).optional(),
  logoUrl: z
    .url({ protocol: /^https?$/ })
    .max(500)
    .or(z.literal('')),
  primaryColor: hexColor,
  secondaryColor: hexColor,
  supportEmail: z.email().max(254).or(z.literal('')),
  websiteUrl: z
    .url({ protocol: /^https?$/ })
    .max(500)
    .or(z.literal('')),
  invitationEmailSubject: z.string().trim().min(1).max(160),
  invitationEmailSignature: z.string().trim().max(500),
  privacyPolicyUrl: z
    .url({ protocol: /^https?$/ })
    .max(500)
    .or(z.literal('')),
});
export const updateGymSettingsSchema = gymSettingsSchema.partial();

export const paginationSchema = z.object({
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
  offset: z.coerce.number().int().min(0).default(0),
});

export type LoginInput = z.infer<typeof loginSchema>;
export type CreateInvitationInput = z.input<typeof createInvitationSchema>;
export type TechniqueInput = z.input<typeof techniqueSchema>;
export type CategoryInput = z.input<typeof categorySchema>;
export type GymSettingsInput = z.infer<typeof gymSettingsSchema>;
